import { TopicsMap, TopicPayload, Step } from '../../../@types/topics';
import {
  IBoostersResponse,
  IWeeklyBoosterWithViewStatusDto
} from '../../../@types/boosters';

/**
 * Parses a raw TopicsMap response from the backend
 * Use this when the backend returns data in TopicsMap format
 * @param rawData - The raw JSON response from the backend
 * @returns Parsed TopicsMap
 */
export function parseTopicsMap (rawData: unknown): TopicsMap {
  if (!rawData || typeof rawData !== 'object') {
    return {};
  }
  return rawData as TopicsMap;
}

/**
 * Converts the existing IBoostersResponse format to TopicsMap format
 * This allows using the new TopicView component with the existing API response
 * @param response - The IBoostersResponse from fetchWeeklyBoosters
 * @returns TopicsMap organized by category/topic name
 */
export function boostersResponseToTopicsMap (
  response: IBoostersResponse | null
): TopicsMap {
  if (!response || !response.boosters || response.boosters.length === 0) {
    return {};
  }

  const topicsMap: TopicsMap = {};

  response.boosters.forEach((booster: IWeeklyBoosterWithViewStatusDto) => {
    // Fallback to empty string to match original behavior (not 'General')
    const topicName =
      (booster.category && booster.category.name) || booster.title || '';

    if (!topicsMap[topicName]) {
      topicsMap[topicName] = [];
    }

    const steps: Step[] =
      booster.content && booster.content.steps
        ? booster.content.steps.map((step) => ({
          order: step.order,
          adviceMessage: step.adviceMessage,
          feedbackMessage: step.feedbackMessage,
        }))
        : [];

    const payload: TopicPayload = {
      objective: (booster.content && booster.content.objective) || '',
      headerLine: (booster.content && booster.content.headerLine) || '',
      steps,
    };

    topicsMap[topicName].push(payload);
  });

  return topicsMap;
}

/**
 * Gets the first topic name from a TopicsMap
 * @param topicsMap - The TopicsMap to get the topic from
 * @returns The first topic name or empty string
 */
export function getFirstTopicName (topicsMap: TopicsMap): string {
  const topicNames = Object.keys(topicsMap);
  return topicNames.length > 0 ? topicNames[0] : '';
}

/**
 * Gets all entries from a TopicsMap as a flat array with topic info
 * Useful for rendering a list of all boosters across topics
 */
export function flattenTopicsMap (
  topicsMap: TopicsMap
): {
  topicName: string;
  entry: TopicPayload;
  index: number;
}[] {
  const result: {
    topicName: string;
    entry: TopicPayload;
    index: number;
  }[] = [];

  Object.entries(topicsMap).forEach(([topicName, entries]) => {
    entries.forEach((entry, index) => {
      result.push({ topicName, entry, index });
    });
  });

  return result;
}
